/**
 * Длительность в записи MediaRecorder.
 *
 * Chrome пишет webm потоком и не знает длину заранее: в заголовке нет
 * элемента Duration. Такой файл проигрывается, но без перемотки и с
 * длительностью «Infinity» — и в браузере, и в скачанном виде. Здесь
 * в Segment › Info дописывается Duration, остальное содержимое не
 * трогается: меняется только начало файла.
 *
 * Если структура не та, которую пишет Chrome (есть SeekHead со
 * смещениями, Duration уже стоит, заголовок не разобрался), файл
 * возвращается как есть — запись важнее перемотки.
 */

const EBML_HEADER = 0x1a45dfa3;
const SEGMENT = 0x18538067;
const SEEK_HEAD = 0x114d9b74;
const INFO = 0x1549a966;
const TIMECODE_SCALE = 0x2ad7b1;
const DURATION = 0x4489;

/** Столько начала файла достаточно, чтобы дойти до Info. */
const HEAD_BYTES = 64 * 1024;

interface Vint {
  value: number;
  length: number;
  /** Все биты значения — единицы: «размер неизвестен». */
  unknown: boolean;
}

function readVint(bytes: Uint8Array, at: number, keepMarker: boolean): Vint | null {
  const first = bytes[at];
  if (first === undefined || first === 0) return null;
  let length = 1;
  while (length <= 8 && (first & (0x80 >> (length - 1))) === 0) length += 1;
  if (length > 8 || at + length > bytes.length) return null;
  let value = keepMarker ? first : first & (0xff >> length);
  let allOnes = value === 0xff >> length;
  for (let i = 1; i < length; i += 1) {
    const byte = bytes[at + i] ?? 0;
    value = value * 256 + byte;
    if (byte !== 0xff) allOnes = false;
  }
  return { value, length, unknown: !keepMarker && allOnes };
}

/** Размер восьмибайтовым vint: длина поля не зависит от значения. */
function sizeVint(value: number): Uint8Array {
  const out = new Uint8Array(8);
  out[0] = 0x01;
  let rest = value;
  for (let i = 7; i >= 1; i -= 1) {
    out[i] = rest % 256;
    rest = Math.floor(rest / 256);
  }
  return out;
}

function readUint(bytes: Uint8Array, at: number, length: number): number {
  let value = 0;
  for (let i = 0; i < length; i += 1) value = value * 256 + (bytes[at + i] ?? 0);
  return value;
}

interface Element {
  id: number;
  /** Начало элемента (его ID). */
  start: number;
  /** Начало и длина поля размера. */
  sizeAt: number;
  sizeLength: number;
  dataStart: number;
  size: Vint;
}

function readElement(bytes: Uint8Array, at: number): Element | null {
  const id = readVint(bytes, at, true);
  if (id === null) return null;
  const size = readVint(bytes, at + id.length, false);
  if (size === null) return null;
  return {
    id: id.value,
    start: at,
    sizeAt: at + id.length,
    sizeLength: size.length,
    dataStart: at + id.length + size.length,
    size,
  };
}

/**
 * Начало файла с дописанной длительностью и место, с которого идёт
 * нетронутый остаток; null — дописывать нечего или нельзя.
 */
export function patchWebmHead(
  head: Uint8Array,
  durationMs: number,
): { head: Uint8Array<ArrayBuffer>; restFrom: number } | null {
  const header = readElement(head, 0);
  if (header?.id !== EBML_HEADER || header.size.unknown) return null;
  const segment = readElement(head, header.dataStart + header.size.value);
  if (segment?.id !== SEGMENT) return null;

  let at = segment.dataStart;
  let info: Element | null = null;
  while (at < head.length) {
    const element = readElement(head, at);
    if (element === null) return null;
    // Смещения SeekHead указывают внутрь сегмента — вставка их сломает.
    if (element.id === SEEK_HEAD) return null;
    if (element.id === INFO) {
      info = element;
      break;
    }
    if (element.size.unknown) return null;
    at = element.dataStart + element.size.value;
  }
  if (info === null || info.size.unknown) return null;
  const infoEnd = info.dataStart + info.size.value;
  if (infoEnd > head.length) return null;

  let timecodeScale = 1_000_000;
  for (let child = info.dataStart; child < infoEnd; ) {
    const element = readElement(head, child);
    if (element === null || element.size.unknown) return null;
    if (element.id === DURATION) return null;
    if (element.id === TIMECODE_SCALE) {
      timecodeScale = readUint(head, element.dataStart, element.size.value);
    }
    child = element.dataStart + element.size.value;
  }
  if (timecodeScale <= 0) return null;

  // Duration — float64 в единицах TimecodeScale (по умолчанию миллисекунды).
  const duration = new Uint8Array(11);
  duration.set([0x44, 0x89, 0x88]);
  new DataView(duration.buffer).setFloat64(3, (durationMs * 1_000_000) / timecodeScale);

  const infoSize = sizeVint(info.size.value + duration.length);
  const grow = duration.length + infoSize.length - info.sizeLength;

  const parts: Uint8Array[] = [];
  if (segment.size.unknown) {
    parts.push(head.subarray(0, info.sizeAt));
  } else {
    parts.push(head.subarray(0, segment.sizeAt));
    parts.push(sizeVint(segment.size.value + grow));
    parts.push(head.subarray(segment.dataStart, info.sizeAt));
  }
  parts.push(infoSize, head.subarray(info.dataStart, infoEnd), duration);

  const out = new Uint8Array(parts.reduce((sum, part) => sum + part.length, 0));
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return { head: out, restFrom: infoEnd };
}

/** Запись с длительностью; при любой неудаче — исходная запись. */
export async function withWebmDuration(blob: Blob, durationMs: number): Promise<Blob> {
  try {
    const head = new Uint8Array(await blob.slice(0, HEAD_BYTES).arrayBuffer());
    const patched = patchWebmHead(head, durationMs);
    if (patched === null) return blob;
    return new Blob([patched.head, blob.slice(patched.restFrom)], { type: blob.type });
  } catch {
    return blob;
  }
}
